import React, { useEffect } from 'react';
import { Form, message } from 'antd';
import CommonDialog from '../../../global/dialog';
import CommonForm from '../../../global/form';
import CommonSelect from '../../../global/select';
import { useGenericMutation } from '../../../../queries/use-generic-mutation';
import { useGenericQuery } from '../../../../queries/use-generic-query';
import { QUERY_KEYS } from '../../../../lib/query-constants';
import { useQueryClient } from '@tanstack/react-query';
import SOURCES from '../../../../utils/constants';
import { capitalizeFirstLetter } from '../../../../utils/functions';

const EnhancedAddAlert = ({ showForm, setShowForm, editAlert, keyParams }) => {
    const [form] = Form.useForm();
    const queryClient = useQueryClient();
    const { ALERT_VIA_EMAIL, ALERT_VIA_SMS, ALERT_VIA_BOTH } = SOURCES;

    const { data: alertDetail, isLoading: alertDetailLoading } = useGenericQuery({
        queryKey: QUERY_KEYS.alerts.item({ id: editAlert.id }),
        queryParams: { id: editAlert.id },
        endpoint: "alert",
        enabled: !!editAlert.id && showForm
    });

    const saveMutation = useGenericMutation({
        endpoint: "alert",
        onSuccess: () => {
            message.success(editAlert.id ? "Alert updated successfully." : "Alert created successfully.");
            setShowForm(false);
            queryClient.invalidateQueries({
                queryKey: QUERY_KEYS.alerts.lists(keyParams),
                type: "active"
            });
        }
    });

    useEffect(() => {
        if (alertDetail && editAlert.id) {
            form.setFieldsValue({
                alertVia: alertDetail.alertVia,
                alertName: alertDetail.alertName,
                alertEmail: alertDetail.alertEmail,
                alertSms: alertDetail.alertSms,
            });
        } else {
            form.resetFields();
        }
    }, [alertDetail, editAlert.id, form, showForm]);

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();
            const postData = {
                ...values,
                id: editAlert.id || ""
            };
            await saveMutation.mutateAsync(postData);
        } catch (error) {
            console.error("Validation failed:", error);
        }
    };

    const alertViaOptions = [
        { label: capitalizeFirstLetter(ALERT_VIA_EMAIL), value: ALERT_VIA_EMAIL },
        { label: capitalizeFirstLetter(ALERT_VIA_SMS), value: ALERT_VIA_SMS },
        {
            label: `${capitalizeFirstLetter(ALERT_VIA_EMAIL)} & ${capitalizeFirstLetter(ALERT_VIA_SMS)}`,
            value: ALERT_VIA_BOTH
        },
    ];

    const formData = [
        {
            label: "Alert Via",
            name: "alertVia",
            inputType: SOURCES.FORMINPUTTYPES.SELECT,
            required: true,
            placeholder: "Select Alert Via",
            options: alertViaOptions,
            span: 12,
        },
        {
            label: "Name",
            name: "alertName",
            inputType: SOURCES.FORMINPUTTYPES.INPUT,
            required: true,
            placeholder: "Enter Name",
            span: 12,
        },
        {
            label: "Email",
            name: "alertEmail",
            inputType: SOURCES.FORMINPUTTYPES.INPUT,
            required: true,
            placeholder: "Enter Email",
            type: "email",
            span: 12,
        },
        {
            label: "Mobile No",
            name: "alertSms",
            inputType: SOURCES.FORMINPUTTYPES.INPUT,
            required: true,
            placeholder: "Enter Mobile No",
            span: 12,
        }
    ];

    return (
        <CommonDialog
            title={editAlert.id ? "Edit Alert" : "Create New Alert"}
            description={editAlert.id ? "Modify the alert details and save your changes." : "Fill the form to create a new Email/SMS alert."}
            open={showForm}
            setOpen={setShowForm}
            onOk={handleSubmit}
            isConfirmButtonLoading={saveMutation.isPending}
            width={500}
            okButtonTitle={editAlert.id ? "Update" : "Create"}
            onCancel={() => setShowForm(false)}
            className="relative"
        >
            {editAlert.id && alertDetailLoading && (
                <div className="absolute -top-2 left-0 w-full h-full bg-black/15 z-10 rounded-lg flex justify-center items-center">
                    <div className="flex flex-col justify-center items-center gap-1">
                        <div className="size-6 rounded-full border-2 border-white !border-t-primary animate-spin" />
                    </div>
                </div>
            )}
            <div className="pt-2">
                <CommonForm
                    data={formData}
                    layoutType="vertical"
                    form={form}
                />
            </div>
        </CommonDialog>
    );
};

export default EnhancedAddAlert;
